import * as os from "node:os";
import * as path from "node:path";

/**
 * SERVICEME user home directory helpers.
 *
 * All paths resolve under {@link getServicemeHome}, which is either the
 * `SERVICEME_HOME` environment variable (when set and non-empty) or
 * `$HOME/.serviceme` on POSIX / `%USERPROFILE%\.serviceme` on Windows.
 *
 * Tests inject `homeDir` and `servicemeHomeEnv` overrides via
 * {@link setUserHomeOverrides} / {@link resetUserHomeOverrides} so they can
 * exercise the path logic without touching the real user environment.
 */

/** Layout constants — kept in one place so other modules can reuse them. */
export const SERVICEME_DIR_NAME = ".serviceme";
export const REPOS_SUBDIR = "repos";
export const CACHE_SUBDIR = "cache";
export const DRAFTS_SUBDIR = "drafts";
export const SKILL_DRAFTS_SUBDIR = "skills";
export const AGENT_DRAFTS_SUBDIR = "agents";
export const REPOS_CONFIG_FILENAME = "repos.json";

/** rev.20 — r7 BYOM Server Proxy toggle self-state (read by ext + CLI + server). */
export const SERVER_PROXY_GLOBAL_FILENAME = "server-proxy.json";

/**
 * Repo id regex — used to validate any `repoId` argument before it is joined
 * into a filesystem path. Keeps path traversal attempts out and gives us a
 * predictable on-disk shape.
 */
export const SAFE_REPO_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/;

/** Environment variable that overrides the user-home root directory. */
export const SERVICEME_HOME_ENV = "SERVICEME_HOME";

/**
 * Test seam: lets unit tests inject deterministic values for `os.homedir()`
 * and the `SERVICEME_HOME` env override without actually mutating
 * `process.env` (which would leak into other tests).
 */
interface UserHomeOverrides {
	homeDir?: string | undefined;
	servicemeHomeEnv?: string | undefined;
	platform?: NodeJS.Platform | undefined;
}

let activeOverrides: UserHomeOverrides = {};

export function setUserHomeOverrides(overrides: UserHomeOverrides): void {
	activeOverrides = { ...overrides };
}

export function resetUserHomeOverrides(): void {
	activeOverrides = {};
}

function resolveHomeDir(): string {
	const injected = activeOverrides.homeDir;
	if (injected !== undefined) {
		return injected;
	}
	return os.homedir();
}

function resolveServicemeHomeEnv(): string | undefined {
	const injected = activeOverrides.servicemeHomeEnv;
	if (injected !== undefined) {
		// Treat empty string as "not set" — `process.env` always returns a string
		// but tests may deliberately pass "" to opt out.
		return injected.length > 0 ? injected : undefined;
	}
	const envValue = process.env[SERVICEME_HOME_ENV];
	return envValue && envValue.length > 0 ? envValue : undefined;
}

function resolvePlatform(): NodeJS.Platform {
	return activeOverrides.platform ?? process.platform;
}

export function assertSafeRepoId(repoId: string): string {
	if (typeof repoId !== "string" || repoId.length === 0 || !SAFE_REPO_ID_PATTERN.test(repoId)) {
		throw new Error(
			`Invalid repo id: ${JSON.stringify(repoId)}. ` +
				`Must match ${SAFE_REPO_ID_PATTERN} (alphanumeric start, then ` +
				`alphanumerics / underscores / hyphens, ≤ 64 chars).`
		);
	}
	return repoId;
}

/**
 * The raw OS home directory (`os.homedir()`), honoring test overrides
 * ({@link setUserHomeOverrides}). Exported for callers that need a
 * home-relative path *outside* of `~/.serviceme` — e.g. the
 * `~/.agents/{skills,agents}` convention used by `SkillLinker` for
 * user-scope links.
 */
export function getHomeDir(): string {
	return resolveHomeDir();
}

/**
 * Root directory for all SERVICEME user-level state. Resolves to
 * `${SERVICEME_HOME}` when that env var is set, otherwise `${HOME}/.serviceme`
 * (POSIX) or `%USERPROFILE%\.serviceme` (Windows via `os.homedir`).
 */
export function getServicemeHome(): string {
	const override = resolveServicemeHomeEnv();
	if (override !== undefined) {
		return path.resolve(override);
	}
	return path.join(resolveHomeDir(), SERVICEME_DIR_NAME);
}

/** `$HOME/.serviceme/repos` (or `${SERVICEME_HOME}/repos`). */
export function getReposDir(): string {
	return path.join(getServicemeHome(), REPOS_SUBDIR);
}

/** `$HOME/.serviceme/repos/<repoId>` — validates `repoId` first. */
export function getRepoDir(repoId: string): string {
	return path.join(getReposDir(), assertSafeRepoId(repoId));
}

/** `$HOME/.serviceme/cache` — generic per-user cache. */
export function getCacheDir(): string {
	return path.join(getServicemeHome(), CACHE_SUBDIR);
}

/** `$HOME/.serviceme/cache/<repoId>` — per-repo sync state lives here. */
export function getRepoCacheDir(repoId: string): string {
	return path.join(getCacheDir(), assertSafeRepoId(repoId));
}

/** `$HOME/.serviceme/drafts` — local edits not yet pushed upstream. */
export function getDraftsDir(): string {
	return path.join(getServicemeHome(), DRAFTS_SUBDIR);
}

/** `$HOME/.serviceme/drafts/skills` — local skill drafts. */
export function getSkillDraftsDir(): string {
	return path.join(getDraftsDir(), SKILL_DRAFTS_SUBDIR);
}

/** `$HOME/.serviceme/drafts/agents` — local agent drafts. */
export function getAgentDraftsDir(): string {
	return path.join(getDraftsDir(), AGENT_DRAFTS_SUBDIR);
}

/**
 * `$HOME/.serviceme/repos.json` — the single config entry point for repo
 * metadata. Always under the resolved home root (i.e. follows
 * `SERVICEME_HOME` overrides too).
 */
export function getReposConfigPath(): string {
	return path.join(getServicemeHome(), REPOS_CONFIG_FILENAME);
}

/**
 * Convenience helper for callers that need to switch behaviour on platform
 * (e.g. `SkillLinker` chooses symlink vs junction). Exported mostly so tests
 * can pin the value without touching `process.platform` directly.
 */
export function getHomePlatform(): NodeJS.Platform {
	return resolvePlatform();
}

// ─── Scheduled Tasks paths (added by M1.5.3) ───────────────────────────────

/** Layout constants for scheduled-tasks files. Kept here so other modules
 * can reuse them and the names stay in sync with the design doc. */
export const SCHEDULED_TASKS_CONFIG_FILENAME = "scheduled-tasks.json";
export const SCHEDULED_TASKS_LOG_FILENAME = "scheduled-tasks-log.json";
export const SCHEDULER_PID_FILENAME = "scheduler.pid";
export const SCHEDULER_LOCK_FILENAME = "scheduler.lock";
export const SCHEDULER_LOG_FILENAME = "scheduler.log";
export const MIGRATION_FAILURES_FILENAME = "migration-failures.json";
export const KNOWN_WORKSPACES_FILENAME = "known-workspaces.json";

/** `~/.serviceme/scheduled-tasks.json` — the global v2 task list. */
export function getScheduledTasksConfigPath(): string {
	return path.join(getServicemeHome(), SCHEDULED_TASKS_CONFIG_FILENAME);
}

/** `~/.serviceme/scheduled-tasks-log.json` — the global execution log (200 LRU). */
export function getScheduledTasksLogPath(): string {
	return path.join(getServicemeHome(), SCHEDULED_TASKS_LOG_FILENAME);
}

/** `~/.serviceme/scheduler.pid` — global daemon PID file. */
export function getSchedulerPidPath(): string {
	return path.join(getServicemeHome(), SCHEDULER_PID_FILENAME);
}

/** `~/.serviceme/scheduler.lock` — global daemon startup flock. */
export function getSchedulerLockPath(): string {
	return path.join(getServicemeHome(), SCHEDULER_LOCK_FILENAME);
}

/** `~/.serviceme/scheduler.log` — global daemon log (daemon lifecycle, not task execution). */
export function getSchedulerLogPath(): string {
	return path.join(getServicemeHome(), SCHEDULER_LOG_FILENAME);
}

/** `~/.serviceme/migration-failures.json` — diagnostics for failed v1→v2 imports. */
export function getMigrationFailuresPath(): string {
	return path.join(getServicemeHome(), MIGRATION_FAILURES_FILENAME);
}

/** `~/.serviceme/known-workspaces.json` — workspace list the extension has seen. */
export function getKnownWorkspacesPath(): string {
	return path.join(getServicemeHome(), KNOWN_WORKSPACES_FILENAME);
}

// ─── r7 BYOM Server Proxy (rev.20) ─────────────────────────────────────────

/**
 * `~/.serviceme/server-proxy.json` — r7 BYOM Server Proxy toggle
 * self-state. Written by the extension's `ProxyConfigService`, readable
 * by the CLI / Server so they can decide whether to route traffic
 * through the corporate proxy without booting VS Code.
 *
 * Lives under user-level `~/.serviceme/` (not under a workspace) because
 * the toggle is a *user preference* — the same human enabling it on
 * machine A should be able to rely on it on machine B after sync, not
 * have it disappear when they switch repos.
 */
export function getServerProxyGlobalPath(): string {
	return path.join(getServicemeHome(), SERVER_PROXY_GLOBAL_FILENAME);
}

// ─── Phase 5 (auth + device + toolbox) placeholders (Spec §11.12) ────────

/** Layout constants for the Phase 5 client-side state files. The server
 * already has the corresponding routes (`/api/v1/auth/...`,
 * `/api/v1/device/...`, etc.) — the client just needs the on-disk
 * file paths + a first-run bootstrap so the auth/device/toolbox
 * services can open + read them without a per-call existence check. */
export const CREDENTIALS_CONFIG_FILENAME = "credentials.json";
export const DEVICE_JSON_FILENAME = "device.json";
export const TOOLBOX_JSON_FILENAME = "toolbox.json";
export const MACHINE_ID_FILENAME = "machine-id";
export const PROFILES_JSON_FILENAME = "profiles.json";

/** `~/.serviceme/credentials.json` — better-auth session tokens + refresh state (client-side cache). */
export function getCredentialsConfigPath(): string {
	return path.join(getServicemeHome(), CREDENTIALS_CONFIG_FILENAME);
}

/** `~/.serviceme/device.json` — device enrollment payload (id, public key fingerprint, claimed-by). */
export function getDeviceJsonPath(): string {
	return path.join(getServicemeHome(), DEVICE_JSON_FILENAME);
}

/** `~/.serviceme/toolbox.json` — local toolbox state (installed tool refs, per-user prefs). */
export function getToolboxJsonPath(): string {
	return path.join(getServicemeHome(), TOOLBOX_JSON_FILENAME);
}

/** `~/.serviceme/machine-id` — opaque stable per-install id (uuid v4 string, no JSON wrapper). */
export function getMachineIdPath(): string {
	return path.join(getServicemeHome(), MACHINE_ID_FILENAME);
}

/** `~/.serviceme/profiles.json` — cached projection of server-side profile rows; refresh on demand. */
export function getProfilesJsonPath(): string {
	return path.join(getServicemeHome(), PROFILES_JSON_FILENAME);
}
